import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ThemeProvider, createTheme, CssBaseline, Box, AppBar, Toolbar,
  Typography, Paper, List, ListItemButton, Chip,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  IconButton, TextField, InputAdornment, useMediaQuery
} from '@mui/material';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import SearchIcon          from '@mui/icons-material/Search';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import FullscreenIcon      from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon  from '@mui/icons-material/FullscreenExit';
import TvIcon              from '@mui/icons-material/Tv';
import cfg  from './config.json';
import i18n from './i18n.json';
import { buildPalette, alpha } from './colorUtils';
import './App.css';

const pal  = buildPalette(cfg.brand.primary, cfg.brand.accent, cfg.brand.base);
const LANGS = ['tr', 'en', 'de'];

const sports = [
  { id: '1'  }, { id: '8'  }, { id: '63' }, { id: '20' }, { id: '3'  },
  { id: '9'  }, { id: '45' }, { id: '26' }, { id: '36' }, { id: '6'  },
];

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: pal.primary },
    secondary:  { main: pal.accent  },
    background: { default: pal.bg.default, paper: pal.bg.paper },
    text:       { primary: pal.text.primary, secondary: pal.text.secondary },
  },
  typography: { fontFamily: '"Inter","Segoe UI",sans-serif' },
  components: {
    MuiPaper:     { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiAccordion: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

/* ─────────────────────────────────────── */

function App() {
  const [matches,      setMatches]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeMatch,  setActiveMatch]  = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [domain,       setDomain]       = useState("beterio.com");
  const [lang,         setLang]         = useState(
    () => localStorage.getItem('lang') || 'tr'
  );
  const playerContainerRef = useRef(null);

  const isMobile      = useMediaQuery(darkTheme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(darkTheme.breakpoints.up(cfg.banners.side.minBreakpoint));

  /* çeviri helper */
  const tx = i18n[lang] || i18n.tr;
  const t  = key => tx[key] ?? key;

  const switchLang = code => {
    setLang(code);
    localStorage.setItem('lang', code);
  };

  useEffect(() => { document.title = cfg.siteTitle; }, []);

  /* ── fetch domain ── */
  // useEffect(() => {
  //   fetch('https://titaapi.privesports.xyz/domain')
  //     .then(r => r.json())
  //     .then(d => { if (d.success) setDomain(d.domain); });
  // }, []);

  /* ── fetch matches ── */
  useEffect(() => {
    fetch('https://geturl.yextof.com/getmatches')
      .then(r => r.json())
      .then(d => {
        if (d.success) setMatches(d.data.map(m => ({ ...m, sportId: m.sportid })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  /* ── fullscreen ── */
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape' && isFullscreen && !document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  useEffect(() => {
    const onChg = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onChg);
    document.addEventListener('webkitfullscreenchange', onChg);
    return () => {
      document.removeEventListener('fullscreenchange', onChg);
      document.removeEventListener('webkitfullscreenchange', onChg);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  useEffect(() => {
    const set = () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    set();
    window.addEventListener('resize', set);
    return () => window.removeEventListener('resize', set);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = playerContainerRef.current || document.documentElement;
    if (!isFullscreen) {
      setIsFullscreen(true);
      try {
        if      (el.requestFullscreen)       await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
        else if (el.msRequestFullscreen)     await el.msRequestFullscreen();
        else { window.scrollTo(0, 1); document.body.style.height = '100vh'; }
      } catch (e) {}
    } else {
      setIsFullscreen(false);
      try {
        if      (document.exitFullscreen)       await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        document.body.style.height = '';
      } catch (e) {}
    }
  }, [isFullscreen]);

  /* ── data ── */
  const grouped = sports.reduce((acc, sport) => {
    const sm = matches.filter(m => String(m.sportId) === String(sport.id));
    if (sm.length > 0) acc.push({ id: sport.id, name: tx.sports[sport.id] || sport.id, matches: sm.sort((a, b) => (b.matchstatus === 'live') - (a.matchstatus === 'live')) });
    return acc;
  }, []);

  const filterMatch = m => {
    if (!searchTerm) return true;
    return `${m.teams?.home || ''} ${m.teams?.away || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const goToDomain = () => domain && (window.location.href = 'https://' + domain);
  const liveCount  = matches.filter(m => m.matchstatus === 'live').length;

  /* ─── Dil Seçici ─── */
  const renderLangSwitcher = () => (
    <Box sx={{ display: 'flex', gap: 0.4, ml: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
      {LANGS.map(code => (
        <Box
          key={code}
          onClick={() => switchLang(code)}
          sx={{
            px: { xs: 0.6, sm: 0.9 },
            py: { xs: 0.25, sm: 0.3 },
            borderRadius: '6px',
            fontSize: { xs: '9px', sm: '10px' },
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.15s',
            bgcolor:   lang === code ? pal.accent     : 'transparent',
            color:     lang === code ? pal.bg.default : pal.text.secondary,
            border:    `1px solid ${lang === code ? pal.accent : pal.border.light}`,
            '&:hover': { borderColor: pal.accent, color: lang === code ? pal.bg.default : pal.accent },
          }}
        >
          {code}
        </Box>
      ))}
    </Box>
  );

  /* ─── Maç Listesi ─── */
  const renderMatchList = () => (
    <Paper
      elevation={0}
      sx={{
        height: { xs: 'auto', md: '100%' },
        display: 'flex', flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        bgcolor: pal.bg.paper,
        border: `1px solid ${pal.border.light}`,
        borderRadius: 2,
        boxShadow: pal.shadow.card,
      }}
    >
      <Box sx={{ p: 1.5, bgcolor: pal.bg.header, borderBottom: `1px solid ${pal.border.light}` }}>
        <Typography sx={{
          display: 'block', mb: 1,
          color: pal.text.secondary, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '10px',
        }}>
          {t('todayMatches')}
        </Typography>
        <TextField
          fullWidth size="small"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: pal.text.secondary, fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: alpha(cfg.brand.base, 0.5),
              borderRadius: '8px', fontSize: '13px',
              '& fieldset':             { borderColor: pal.border.light },
              '&:hover fieldset':       { borderColor: pal.border.medium },
              '&.Mui-focused fieldset': { borderColor: pal.accent },
            },
            '& .MuiInputBase-input': { color: pal.text.primary },
          }}
        />
      </Box>

      <Box sx={{ flexGrow: { xs: 0, md: 1 }, overflowY: { xs: 'visible', md: 'auto' }, p: 0.75 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 6 }}>
            <CircularProgress size={28} sx={{ color: pal.accent }} />
          </Box>
        ) : grouped.length === 0 ? (
          <Typography align="center" sx={{ mt: 4, color: pal.text.secondary, fontSize: '13px' }}>
            {t('noMatches')}
          </Typography>
        ) : (
          grouped.map(group => {
            const filtered = group.matches.filter(filterMatch);
            if (!filtered.length) return null;
            return (
              <Accordion
                key={group.id} defaultExpanded disableGutters
                sx={{ bgcolor: 'transparent', backgroundImage: 'none', boxShadow: 'none', '&:before': { display: 'none' }, mb: 0.5 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: pal.text.secondary, fontSize: 18 }} />}
                  sx={{
                    minHeight: 36, px: 1.25,
                    bgcolor: pal.bg.elevated,
                    borderRadius: '7px',
                    border: `1px solid ${pal.border.light}`,
                    '&.Mui-expanded': { borderRadius: '7px 7px 0 0' },
                    '& .MuiAccordionSummary-content': { my: 0.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: pal.text.primary, letterSpacing: '0.02em' }}>
                      {group.name}
                    </Typography>
                    <Box sx={{
                      px: 0.75, borderRadius: '10px', bgcolor: pal.chip.bg,
                      fontSize: '10px', fontWeight: 700, color: pal.chip.color,
                      lineHeight: '18px', minWidth: 18, textAlign: 'center',
                    }}>
                      {filtered.length}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{
                  p: 0,
                  bgcolor: alpha(cfg.brand.base, 0.3),
                  borderRadius: '0 0 7px 7px',
                  border: `1px solid ${pal.border.light}`,
                  borderTop: 'none',
                }}>
                  <List disablePadding sx={{ py: 0.5 }}>
                    {filtered.map(match => {
                      const isLive     = match.matchstatus === 'live';
                      const isSelected = activeMatch?.id === match.id;
                      return (
                        <ListItemButton
                          key={match.id} selected={isSelected}
                          onClick={() => setActiveMatch(match)}
                          sx={{
                            mx: 0.5, mb: 0.25, borderRadius: '6px',
                            py: 0.75, px: 1,
                            bgcolor: isSelected ? pal.selected.bg : 'transparent',
                            borderLeft: `3px solid ${isSelected ? pal.accent : 'transparent'}`,
                            transition: 'all 0.15s ease',
                            '&:hover': { bgcolor: pal.selected.hover },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                            {isLive && <span className="live-dot" />}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontSize: '12px', fontWeight: 600, color: pal.text.primary,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {match.teams?.home || t('home')}
                              </Typography>
                              <Typography sx={{
                                fontSize: '10px', color: pal.text.secondary,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                vs {match.teams?.away || t('away')}
                              </Typography>
                            </Box>
                            <Chip
                              label={isLive ? t('live') : t('upcoming')}
                              size="small"
                              sx={{
                                height: 16, fontSize: '9px', fontWeight: 700, flexShrink: 0,
                                bgcolor: isLive ? pal.live.bg    : pal.upcoming.bg,
                                color:   isLive ? pal.live.color : pal.upcoming.color,
                                border:  `1px solid ${isLive ? pal.live.border : pal.upcoming.border}`,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          </Box>
                        </ListItemButton>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Paper>
  );

  /* ─── Video Oynatıcı ─── */
  const renderPlayer = () => (
    <Box
      ref={playerContainerRef}
      sx={{
        bgcolor: pal.bg.player,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        ...(isFullscreen ? {
          position: 'fixed', top: 0, left: 0,
          width: '100dvw', height: '100dvh',
          zIndex: 999999, background: '#000',
        } : {
          position: 'relative', flexGrow: 1, height: '100%',
          borderRadius: 2, border: `1px solid ${pal.border.light}`,
          boxShadow: pal.shadow.card,
        }),
      }}
    >
      {!activeMatch ? (
        <Box sx={{ textAlign: 'center', px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <img
            src={cfg.logo.src} alt={cfg.logo.alt}
            style={{ height: 56, width: 'auto', objectFit: 'contain', opacity: 0.35 }}
          />
          <Box>
            <TvIcon sx={{ fontSize: 28, color: alpha(cfg.brand.primary, 0.5), mb: 0.5 }} />
            <Typography sx={{ color: pal.text.secondary, fontSize: '13px' }}>
              {isMobile ? t('selectMobile') : t('selectDesktop')}
            </Typography>
          </Box>
        </Box>
      ) : activeMatch.fullscreen ? (
        <>
          <iframe
            title="Match Stream" src={activeMatch.url}
            style={{ width: '100%', height: '100%', border: 'none' }}
            allow="fullscreen; autoplay" allowFullScreen
          />
          <Box sx={{
            position: 'absolute', bottom: 12, right: 12,
            display: 'flex', alignItems: 'center', gap: 1,
            zIndex: 10,
            bgcolor: 'rgba(10,22,40,0.78)', backdropFilter: 'blur(8px)',
            borderRadius: '10px', px: 1.5, py: 0.75,
            border: `1px solid ${pal.border.accent}`,
            opacity: 0.82, transition: 'opacity 0.2s',
            '&:hover': { opacity: 1 },
          }}>
            <img
              src={cfg.logo.src} alt={cfg.logo.alt}
              style={{ height: cfg.logo.playerHeight, width: 'auto', objectFit: 'contain' }}
            />
            <IconButton
              onClick={toggleFullscreen} size="small"
              sx={{ color: pal.accent, p: 0.4, '&:hover': { bgcolor: pal.upcoming.bg } }}
            >
              {isFullscreen
                ? <FullscreenExitIcon sx={{ fontSize: 20 }} />
                : <FullscreenIcon     sx={{ fontSize: 20 }} />
              }
            </IconButton>
          </Box>
        </>
      ) : (
        <Typography color="error" variant="body2" align="center" sx={{ px: 2 }}>
          {t('noStream')}
        </Typography>
      )}
    </Box>
  );

  /* ─── Yan Banner ─── */
  const renderSideBanner = () => {
    const b = cfg.banners.side;
    if (!b.show || !isLargeScreen) return null;
    return (
      <Box
        onClick={goToDomain}
        sx={{
          width: b.width, flexShrink: 0, order: 1,
          overflow: 'hidden', borderRadius: 2, lineHeight: 0,
          cursor: domain ? 'pointer' : 'default',
          alignSelf: 'stretch',
          border: `1px solid ${pal.border.light}`,
        }}
      >
        <img src={b.src} alt={b.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </Box>
    );
  };

  /* ─── Alt Banner ─── */
  const renderBottomBanner = () => {
    const b = cfg.banners.bottom;
    if (!b.show) return null;
    const maxH = isMobile ? b.mobileMaxHeight : b.desktopMaxHeight;
    return (
      <Box
        onClick={goToDomain}
        sx={{ width: '100%', overflow: 'hidden', lineHeight: 0, flexShrink: 0, px: 1, pb: 1, cursor: domain ? 'pointer' : 'default' }}
      >
        <img
          src={b.src} alt={b.alt}
          style={{ width: '100%', height: 'auto', maxHeight: maxH, objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      </Box>
    );
  };

  /* ─── Ana Render ─── */
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Box sx={{
        display: 'flex', flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(var(--vh, 1vh) * 100)' },
        minHeight: { xs: '100dvh', md: 'unset' },
        overflow: { xs: 'visible', md: 'hidden' },
        bgcolor: pal.bg.default,
      }}>

        {/* ── HEADER ── */}
        <AppBar position="sticky" elevation={0} sx={{ top: 0, zIndex: 100,
          background: pal.gradient.appBar,
          borderBottom: `1px solid ${pal.border.appBar}`,
          boxShadow: pal.shadow.appBar,
        }}>
          <Toolbar variant="dense" sx={{ minHeight: { xs: 52, sm: 56 }, px: { xs: 1.5, sm: 2 } }}>

            {isMobile && activeMatch && (
              <IconButton edge="start" size="small" onClick={() => setActiveMatch(null)} sx={{ mr: 1, color: pal.text.primary }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}

            {/* Logo → ana sayfa */}
            <Box
              onClick={() => setActiveMatch(null)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { xs: 1, sm: 1.5 }, flexShrink: 0 }}
            >
              <img
                src={cfg.logo.src} alt={cfg.logo.alt}
                style={{ height: isMobile ? 26 : cfg.logo.headerHeight, width: 'auto', objectFit: 'contain', display: 'block' }}
              />
            </Box>

            {/* Siteye Git butonu */}
            <Box
              onClick={() => window.open(cfg.siteUrl, '_blank')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                flexShrink: 0, mr: { xs: 1, sm: 2 },
                px: { xs: 0.9, sm: 1.25 }, py: { xs: 0.3, sm: 0.4 },
                borderRadius: '7px',
                border: `1px solid ${pal.border.accent}`,
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: pal.upcoming.bg, borderColor: pal.accent },
              }}
            >
              <Typography sx={{
                fontSize: { xs: '10px', sm: '11px' },
                fontWeight: 700,
                color: pal.accent,
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
              }}>
                {t('visitSite')}
              </Typography>
            </Box>

            {/* Ayırıcı + Başlık — sadece sm+ */}
            <Box sx={{ width: '1px', height: 24, bgcolor: pal.border.medium, mr: 2, display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: pal.text.primary, lineHeight: 1, fontSize: '15px' }}>
                {t('title')}{' '}
                <Box component="span" sx={{ color: pal.accent }}>{t('titleAccent')}</Box>
              </Typography>
              <Typography sx={{ fontSize: '10px', color: pal.text.secondary, letterSpacing: '0.06em', mt: 0.2 }}>
                {t('subtitle')}
              </Typography>
            </Box>

            {/* Boşluk doldurucu — sadece mobilde */}
            <Box sx={{ flexGrow: 1, display: { xs: 'block', sm: 'none' } }} />

            {/* Canlı sayacı — sadece sm+ */}
            {!loading && liveCount > 0 && (
              <Box sx={{
                display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75,
                bgcolor: pal.live.bg, border: `1px solid ${pal.live.border}`,
                borderRadius: '20px', px: 1.25, py: 0.4,
                mr: 1, flexShrink: 0,
              }}>
                <span className="live-dot" style={{ width: 6, height: 6 }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: pal.live.color }}>
                  {t('liveCount').replace('{n}', liveCount)}
                </Typography>
              </Box>
            )}

            {/* Dil seçici */}
            {renderLangSwitcher()}

          </Toolbar>
        </AppBar>

        {/* ── ANA İÇERİK ── */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexGrow: { xs: 0, md: 1 }, overflow: { xs: 'visible', md: 'hidden' }, p: 1, gap: 1 }}>
          {renderSideBanner()}

          <Box sx={{
            order: { xs: 2, md: 2 },
            width: { xs: '100%', md: '280px' },
            flexShrink: 0,
            display: 'flex', flexDirection: 'column',
            minHeight: { xs: 'unset', md: 0 },
          }}>
            {renderMatchList()}
          </Box>

          {(!isMobile || (isMobile && activeMatch)) && (
            <Box sx={{
              order: { xs: 1, md: 3 },
              flexGrow: { xs: 0, md: 1 },
              height: { xs: '240px', sm: '300px', md: '100%' },
              minHeight: { xs: '240px', sm: '300px', md: 'auto' },
              flexShrink: 0,
            }}>
              {renderPlayer()}
            </Box>
          )}
        </Box>

        {renderBottomBanner()}
      </Box>
    </ThemeProvider>
  );
}

export default App;
